// Progressive data hook for the report-style MI page. Each underlying
// fetch resolves independently and emits its result into state; the
// consuming UI watches per-piece ready flags to render skeleton vs.
// real content.
//
// Phases:
//   1. Cluster fetches (places, all product rows, cluster perf) fire on
//      mount in parallel.
//   2. Per-market fetches (campaigns, place zips, city zips) fire in
//      parallel for each market.
//   3. Derived per-market data (topProducts, topCategories) is computed
//      whenever (a) that market's placeZips arrive OR (b) the cluster
//      allProductRows arrives.
//
// State shape:
//   {
//     placesLoading, placeIndex,
//     productsLoading, allProductRows,
//     clusterPerfLoading, clusterTopProducts,
//     marketData: {
//       [market]: {
//         rows, rowsLoading,
//         place, placeZips, placeZipsLoading,
//         cityZips, cityZipsLoading,
//         topProducts, topCategories
//       }
//     },
//     error
//   }

import React from 'react';
import MiStats, { rangeToWindow, rangeToPreset, previousWindow } from '../../endpoints/mi-stats';
import { CAMPAIGN_ONLY_MARKETS, NO_CITY_RESOLVED_TABLE, FALLBACK_ZIPS, ZIP_SCOPED_MARKET } from './report-config';
import { aggregateProductsAndCategoriesForMarket, synthesizePlaceStats } from './aggregations';
function unwrapItems(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.items)) return resp.items;
  return [];
}
function indexPlacesByName(rows) {
  const out = {};
  for (const r of rows) {
    const name = (r.name || '').trim();
    if (!name) continue;
    out[name] = {
      name,
      views: Number(r.views) || 0,
      sessions: Number(r.sessions) || 0,
      uniqueUsers: Number(r.uniqueUsers) || 0,
      conversions: Number(r.conversions) || 0,
      avgTimeSpan: Number(r.avgTimeSpan) || 0
    };
  }
  return out;
}
function initialMarketData(markets) {
  const md = {};
  for (const m of markets) {
    md[m] = {
      market: m,
      rows: [],
      rowsLoading: true,
      prevRows: [],
      prevRowsLoading: true,
      place: null,
      prevPlace: null,
      placeZips: [],
      placeZipsLoading: true,
      cityZips: [],
      cityZipsLoading: true,
      topProducts: [],
      topCategories: []
    };
  }
  return md;
}
function initialState(markets) {
  return {
    placesLoading: true,
    placeIndex: {},
    prevPlacesLoading: true,
    prevPlaceIndex: {},
    productsLoading: true,
    allProductRows: [],
    clusterPerfLoading: true,
    clusterTopProducts: [],
    marketData: initialMarketData(markets),
    error: null
  };
}

// `markets` drives every per-market loop. R&S passes the static report-config
// list; derived tenants pass their live location names (may arrive async —
// the effect re-runs when the list changes).
export default function useReportData({
  range,
  comparison,
  markets
}) {
  const marketsKey = (markets || []).join('|');
  const [state, setState] = React.useState(() => initialState(markets || []));
  React.useEffect(() => {
    let cancelled = false;
    setState(initialState(markets || []));
    const win = rangeToWindow(range);
    if (!win) {
      // all_time (no explicit dates) or an unrecognized token. Surface
      // it instead of fetching a default window under the wrong label.
      setState(s => ({
        ...initialState(markets || []),
        placesLoading: false,
        prevPlacesLoading: false,
        productsLoading: false,
        clusterPerfLoading: false,
        error: range === 'all_time' ? 'All Time is not available on this page — pick a bounded range (e.g. This Year or a custom window).' : `Unsupported date range "${range}".`
      }));
      return () => {
        cancelled = true;
      };
    }
    const {
      start,
      end
    } = win;
    const preset = rangeToPreset(range);
    const prev = previousWindow(range, comparison);

    // Helpers that close over `cancelled` + `setState`.
    const safeSet = updater => {
      if (!cancelled) setState(updater);
    };

    // Patch a per-market entry, then recompute derived fields. Called
    // both when raw fetches resolve and when cluster data lands.
    const patchMarket = (market, patch) => {
      safeSet(s => {
        const prev = s.marketData[market] || initialMarketData(markets)[market];
        const merged = {
          ...prev,
          ...patch
        };

        // Derive place if we have placeZips and no place set yet.
        if (!merged.place && !merged.placeZipsLoading) {
          if (s.placeIndex[market]) {
            merged.place = s.placeIndex[market];
          } else if (merged.placeZips.length > 0) {
            merged.place = synthesizePlaceStats(market, merged.placeZips);
          }
        }

        // Derive topProducts/topCategories if placeZips + allProductRows are ready.
        if (!merged.placeZipsLoading && !s.productsLoading && !CAMPAIGN_ONLY_MARKETS.has(market)) {
          const {
            topProducts,
            topCategories
          } = aggregateProductsAndCategoriesForMarket(market, merged.placeZips, s.allProductRows);
          merged.topProducts = topProducts;
          merged.topCategories = topCategories;
        }
        return {
          ...s,
          marketData: {
            ...s.marketData,
            [market]: merged
          }
        };
      });
    };

    // --- Cluster fetches ---------------------------------------------------

    MiStats.getPlaceStats({
      start,
      end
    }).then(resp => {
      if (cancelled) return;
      const placeIndex = indexPlacesByName(unwrapItems(resp));
      safeSet(s => {
        // Fill in `place` for any market that doesn't have one yet.
        const newMD = {
          ...s.marketData
        };
        for (const m of markets) {
          if (!newMD[m].place && placeIndex[m]) {
            newMD[m] = {
              ...newMD[m],
              place: placeIndex[m]
            };
          }
        }
        return {
          ...s,
          placeIndex,
          placesLoading: false,
          marketData: newMD
        };
      });
    }).catch(() => safeSet(s => ({
      ...s,
      placesLoading: false
    })));

    // Previous-window places (for comparison column in Snapshot + Tracked Website Usage)
    if (prev) {
      MiStats.getPlaceStats({
        start: prev.start,
        end: prev.end
      }).then(resp => {
        if (cancelled) return;
        const prevPlaceIndex = indexPlacesByName(unwrapItems(resp));
        safeSet(s => {
          const newMD = {
            ...s.marketData
          };
          for (const m of markets) {
            if (prevPlaceIndex[m]) {
              newMD[m] = {
                ...newMD[m],
                prevPlace: prevPlaceIndex[m]
              };
            }
          }
          return {
            ...s,
            prevPlaceIndex,
            prevPlacesLoading: false,
            marketData: newMD
          };
        });
      }).catch(() => safeSet(s => ({
        ...s,
        prevPlacesLoading: false
      })));
    } else {
      safeSet(s => ({
        ...s,
        prevPlacesLoading: false
      }));
    }
    MiStats.getProductLocationStats({
      start,
      end
    }).then(resp => {
      if (cancelled) return;
      const allProductRows = unwrapItems(resp);
      safeSet(s => {
        // Recompute topProducts/topCategories for any market whose
        // placeZips are already in.
        const newMD = {
          ...s.marketData
        };
        for (const m of markets) {
          const entry = newMD[m];
          if (!entry.placeZipsLoading && !CAMPAIGN_ONLY_MARKETS.has(m)) {
            const {
              topProducts,
              topCategories
            } = aggregateProductsAndCategoriesForMarket(m, entry.placeZips, allProductRows);
            newMD[m] = {
              ...entry,
              topProducts,
              topCategories
            };
          }
        }
        return {
          ...s,
          allProductRows,
          productsLoading: false,
          marketData: newMD
        };
      });
    }).catch(() => safeSet(s => ({
      ...s,
      productsLoading: false
    })));
    if (preset) {
      MiStats.getClusterProductPerformance({
        preset
      }).then(resp => {
        if (cancelled) return;
        safeSet(s => ({
          ...s,
          clusterTopProducts: unwrapItems(resp),
          clusterPerfLoading: false
        }));
      }).catch(() => safeSet(s => ({
        ...s,
        clusterPerfLoading: false
      })));
    } else {
      safeSet(s => ({
        ...s,
        clusterPerfLoading: false
      }));
    }

    // --- Per-market fetches ------------------------------------------------

    for (const market of markets) {
      // Campaign rows. Wellen Park's data lives in Google Ads — see
      // PerMarketSections in index.jsx for the explanation rendered there.
      const campPromise = market === ZIP_SCOPED_MARKET ? Promise.resolve({
        items: []
      }) : MiStats.getCampaignStats({
        location: market,
        start,
        end
      }).catch(() => ({
        items: []
      }));
      campPromise.then(resp => {
        if (cancelled) return;
        patchMarket(market, {
          rows: unwrapItems(resp),
          rowsLoading: false
        });
      });

      // Previous-window campaign rows for this market (for the
      // cluster snapshot's Previous column — Tracked Website Usage's
      // prev numbers come from prevPlace).
      if (prev && market !== ZIP_SCOPED_MARKET) {
        MiStats.getCampaignStats({
          location: market,
          start: prev.start,
          end: prev.end
        }).then(resp => {
          if (cancelled) return;
          patchMarket(market, {
            prevRows: unwrapItems(resp),
            prevRowsLoading: false
          });
        }).catch(() => patchMarket(market, {
          prevRows: [],
          prevRowsLoading: false
        }));
      } else {
        patchMarket(market, {
          prevRows: [],
          prevRowsLoading: false
        });
      }
      if (CAMPAIGN_ONLY_MARKETS.has(market)) {
        // Outlet: no place/zip data — short-circuit those.
        patchMarket(market, {
          placeZips: [],
          placeZipsLoading: false,
          cityZips: [],
          cityZipsLoading: false
        });
        continue;
      }

      // Place zips: live → fallback if empty.
      (async () => {
        let placeZips = [];
        try {
          const resp = await MiStats.getZipsByPlace({
            place: market,
            start,
            end
          });
          placeZips = unwrapItems(resp);
        } catch (e) {
          placeZips = [];
        }
        if (placeZips.length === 0 && FALLBACK_ZIPS[market]) {
          try {
            const fb = await MiStats.getZipsByList({
              zips: FALLBACK_ZIPS[market],
              start,
              end
            });
            placeZips = unwrapItems(fb);
          } catch (e) {
            // keep empty
          }
        }
        if (cancelled) return;
        placeZips = placeZips.sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 15);
        patchMarket(market, {
          placeZips,
          placeZipsLoading: false
        });
      })();

      // City-resolved zips
      if (NO_CITY_RESOLVED_TABLE.has(market)) {
        patchMarket(market, {
          cityZips: [],
          cityZipsLoading: false
        });
      } else {
        MiStats.getZipsByCity({
          city: market,
          start,
          end
        }).then(resp => {
          if (cancelled) return;
          const cityZips = unwrapItems(resp).sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 10);
          patchMarket(market, {
            cityZips,
            cityZipsLoading: false
          });
        }).catch(() => patchMarket(market, {
          cityZips: [],
          cityZipsLoading: false
        }));
      }
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, comparison, marketsKey]);
  return state;
}